import React, { useState, useEffect } from 'react';
import {
    Table, Button, Tag, Typography, Card, Space, Modal, Descriptions,
    message, DatePicker, Form, InputNumber, Input, Row, Col,
    Statistic, Progress, Divider, Grid, Popconfirm
} from 'antd';
import {
    PlusOutlined, EyeOutlined, PlayCircleOutlined, CheckCircleOutlined,
    FireOutlined, ReloadOutlined, DeleteOutlined
} from '@ant-design/icons';
import ExportButton from '../components/ExportButton';
import { useNavigate } from 'react-router-dom';
import client from '../api/client';
import dayjs from 'dayjs';
import { useAuth } from '../context/AuthContext';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const statusColor = { DRAFT: 'default', IN_PROGRESS: 'orange', COMPLETED: 'green', CANCELLED: 'red' };
const methodColor = { SUN: 'gold', MECHANICAL: 'blue' };

const fmt = (v) => Number(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const getFileNumbers = (order) => [...new Set(
    (order.lots || [])
        .map(lot => lot.palay_lot?.lot_number)
        .filter(Boolean)
)];

const DryingPage = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const { user } = useAuth();
    const isManagerOrAdmin = ['ADMIN', 'MANAGER'].includes(user?.role);
    const navigate = useNavigate();

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dates, setDates] = useState([dayjs().startOf('month'), dayjs().endOf('month')]);
    const [rowLoading, setRowLoading] = useState({});

    // View modal
    const [viewOpen, setViewOpen] = useState(false);
    const [viewOrder, setViewOrder] = useState(null);
    const [viewLoading, setViewLoading] = useState(false);

    // Complete modal
    const [completeOpen, setCompleteOpen] = useState(false);
    const [completeOrder, setCompleteOrder] = useState(null);
    const [completeForm] = Form.useForm();
    const [actionLoading, setActionLoading] = useState(false);

    useEffect(() => { fetchOrders(); }, [dates]);

    const fetchOrders = async () => {
        setLoading(true);
        try {
            const { data } = await client.get('/transactions/drying');
            const allOrders = Array.isArray(data) ? data : [];

            if (!dates) {
                setOrders(allOrders);
                return;
            }

            const startDate = dates[0].startOf('day');
            const endDate = dates[1].endOf('day');
            setOrders(allOrders.filter((order) => {
                // Legacy orders could be created without a scheduled date.
                // Use their creation date so they do not disappear from the list.
                const displayDate = order.scheduled_date || order.created_at;
                if (!displayDate) return false;
                const orderDate = dayjs(displayDate);
                return !orderDate.isBefore(startDate) && !orderDate.isAfter(endDate);
            }));
        } catch {
            message.error('Failed to load drying orders');
        } finally {
            setLoading(false);
        }
    };

    const viewDetail = async (id) => {
        setViewLoading(true);
        setViewOpen(true);
        try {
            const { data } = await client.get(`/transactions/drying/${id}`);
            setViewOrder(data);
        } catch {
            message.error('Failed to load details');
        } finally {
            setViewLoading(false);
        }
    };

    const handleStart = async (id) => {
        setRowLoading(p => ({ ...p, [id]: 'start' }));
        try {
            await client.post(`/transactions/drying/${id}/start`);
            message.success('Drying started');
            fetchOrders();
        } catch (err) {
            message.error(err.response?.data?.message || 'Failed to start drying');
        } finally {
            setRowLoading(p => ({ ...p, [id]: false }));
        }
    };

    const openComplete = async (id) => {
        setViewLoading(true);
        try {
            const { data } = await client.get(`/transactions/drying/${id}`);
            setCompleteOrder(data);
            // Only pre-fill a calculated expectation when moisture readings exist.
            const initial = {};
            data.lots.forEach(lot => {
                initial[`dried_weight_${lot.id}`] = lot.expected_dried_weight ?? undefined;
                initial[`final_mc_${lot.id}`] = undefined;
            });
            completeForm.setFieldsValue(initial);
            setCompleteOpen(true);
        } catch {
            message.error('Failed to load drying order');
        } finally {
            setViewLoading(false);
        }
    };

    const submitComplete = async (values) => {
        setActionLoading(true);
        try {
            const lots = completeOrder.lots.map(lot => ({
                drying_order_lot_id: lot.id,
                dried_weight: values[`dried_weight_${lot.id}`],
                final_mc: values[`final_mc_${lot.id}`],
            }));
            await client.post(`/transactions/drying/${completeOrder.id}/complete`, { lots });
            message.success('Drying completed successfully');
            setCompleteOpen(false);
            fetchOrders();
        } catch (err) {
            message.error(err.response?.data?.message || 'Failed to complete drying');
        } finally {
            setActionLoading(false);
        }
    };

    const handleCancel = async (id) => {
        setRowLoading(p => ({ ...p, [id]: 'cancel' }));
        try {
            await client.post(`/transactions/drying/${id}/cancel`);
            message.success('Drying order cancelled and lots released');
            fetchOrders();
        } catch (err) {
            message.error(err.response?.data?.message || 'Failed to cancel drying order');
        } finally {
            setRowLoading(p => ({ ...p, [id]: false }));
        }
    };

    const handleDelete = async (id) => {
        try {
            await client.delete(`/transactions/drying/${id}`);
            message.success('Drying order deleted');
            fetchOrders();
        } catch (err) {
            message.error(err.response?.data?.message || 'Failed to delete');
        }
    };

    const columns = [
        {
            title: 'Drying #',
            dataIndex: 'drying_number',
            render: (n, r) => <Button type="link" style={{ padding: 0, fontWeight: 600 }} onClick={() => viewDetail(r.id)}>{n}</Button>,
        },
        {
            title: 'Method',
            dataIndex: 'drying_method',
            render: (m) => <Tag color={methodColor[m]}>{m === 'SUN' ? '☀️ Sun Drying' : '⚙️ Mechanical'}</Tag>,
        },
        { title: 'Facility', dataIndex: 'facility_name', render: (v) => v || <Text type="secondary">—</Text> },
        { title: 'Scheduled', dataIndex: 'scheduled_date', render: (d) => d ? dayjs(d).format('MMM D, YYYY') : '—' },
        {
            title: 'Wet Weight (kg)',
            dataIndex: 'total_wet_weight',
            align: 'right',
            render: (v) => <Text strong>{fmt(v)}</Text>,
        },
        {
            title: 'Dried Weight (kg)',
            dataIndex: 'total_dried_weight',
            align: 'right',
            render: (v) => v != null ? <Text style={{ color: '#059669' }}>{fmt(v)}</Text> : <Text type="secondary">—</Text>,
        },
        {
            title: 'Drying Loss',
            dataIndex: 'drying_loss_percent',
            align: 'right',
            render: (v) => v != null
                ? <Tag color={v > 15 ? 'red' : v > 10 ? 'orange' : 'green'}>{fmt(v)}%</Tag>
                : <Text type="secondary">—</Text>,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            render: (s) => <Tag color={statusColor[s]}>{s.replace('_', ' ')}</Tag>,
        },
        {
            title: 'File No.',
            key: 'file_numbers',
            render: (_, order) => {
                const fileNumbers = getFileNumbers(order);
                return fileNumbers.length > 0 ? fileNumbers.join(', ') : <Text type="secondary">—</Text>;
            },
        },
        {
            title: 'Lots',
            dataIndex: 'lots',
            align: 'center',
            render: (lots) => lots?.length ?? 0,
        },
        {
            title: 'Actions',
            align: 'right',
            render: (_, r) => (
                <Space size={4}>
                    <Button size="small" icon={<EyeOutlined />} onClick={() => viewDetail(r.id)}>View</Button>
                    {r.status === 'DRAFT' && (
                        <Button
                            size="small"
                            type="primary"
                            icon={<PlayCircleOutlined />}
                            loading={rowLoading[r.id] === 'start'}
                            onClick={() => handleStart(r.id)}
                        >
                            Start
                        </Button>
                    )}
                    {r.status === 'IN_PROGRESS' && (
                        <Button
                            size="small"
                            type="primary"
                            icon={<CheckCircleOutlined />}
                            style={{ background: '#059669', borderColor: '#059669' }}
                            onClick={() => openComplete(r.id)}
                        >
                            Complete
                        </Button>
                    )}
                    {r.status === 'IN_PROGRESS' && isManagerOrAdmin && (
                        <Popconfirm
                            title="Cancel this drying order?"
                            description="The lots will be released back to UNDRIED."
                            onConfirm={() => handleCancel(r.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button size="small" danger loading={rowLoading[r.id] === 'cancel'}>Cancel</Button>
                        </Popconfirm>
                    )}
                    {r.status === 'DRAFT' && isManagerOrAdmin && (
                        <Popconfirm title="Delete this drying order?" onConfirm={() => handleDelete(r.id)} okText="Yes" cancelText="No">
                            <Button size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: isMobile ? '16px' : '24px 32px' }}>
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <Title level={2} style={{ margin: 0, fontWeight: 800 }}>Drying Operations</Title>
                    <Text style={{ color: 'var(--text-secondary)', fontSize: 16 }}>
                        Manage palay drying — from wet palay to dried palay ready for milling.
                    </Text>
                </div>
                <Space wrap>
                    <RangePicker value={dates} onChange={setDates} style={{ borderRadius: 8 }} />
                    <ExportButton
                        filename="drying-operations"
                        sheetName="Drying"
                        title="Drying Operations"
                        headers={['Drying #', 'Method', 'Facility', 'Scheduled Date', 'Wet Weight (kg)', 'Dried Weight (kg)', 'Drying Loss %', 'Status', 'File No.']}
                        getData={() => orders.map(o => [
                            o.drying_number,
                            o.drying_method,
                            o.facility_name,
                            dayjs(o.scheduled_date).format('YYYY-MM-DD'),
                            o.total_wet_weight,
                            o.total_dried_weight || '',
                            o.drying_loss_percent ? `${Number(o.drying_loss_percent).toFixed(2)}%` : '',
                            o.status,
                            getFileNumbers(o).join(', '),
                        ])}
                        style={{ height: 40, borderRadius: 8 }}
                    />
                    <Button icon={<ReloadOutlined />} onClick={fetchOrders} style={{ height: 40, borderRadius: 8 }} />
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/drying/new')} style={{ height: 40, borderRadius: 8, fontWeight: 600 }}>
                        New Drying Order
                    </Button>
                </Space>
            </div>

            <Card className="premium-card" bodyStyle={{ padding: 0 }}>
                <Table
                    dataSource={orders}
                    columns={columns}
                    loading={loading}
                    rowKey="id"
                    pagination={{ defaultPageSize: 20, showSizeChanger: true }}
                    scroll={{ x: 1050 }}
                />
            </Card>

            {/* View Detail Modal */}
            <Modal
                open={viewOpen}
                onCancel={() => setViewOpen(false)}
                title={viewOrder ? `Drying Order — ${viewOrder.drying_number}` : 'Drying Order'}
                footer={<Button onClick={() => setViewOpen(false)}>Close</Button>}
                width={700}
                loading={viewLoading}
            >
                {viewOrder && (
                    <div>
                        <Descriptions bordered column={2} size="small" style={{ marginBottom: 20 }}>
                            <Descriptions.Item label="Status">
                                <Tag color={statusColor[viewOrder.status]}>{viewOrder.status}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Method">
                                <Tag color={methodColor[viewOrder.drying_method]}>{viewOrder.drying_method}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Facility">{viewOrder.facility_name || '—'}</Descriptions.Item>
                            <Descriptions.Item label="Operator">{viewOrder.operator_name || '—'}</Descriptions.Item>
                            <Descriptions.Item label="Scheduled">{viewOrder.scheduled_date ? dayjs(viewOrder.scheduled_date).format('MMM D, YYYY') : '—'}</Descriptions.Item>
                            <Descriptions.Item label="Target MC">{viewOrder.target_mc != null ? `${viewOrder.target_mc}%` : 'Not measured'}</Descriptions.Item>
                            {viewOrder.started_at && <Descriptions.Item label="Started">{dayjs(viewOrder.started_at).format('MMM D, YYYY HH:mm')}</Descriptions.Item>}
                            {viewOrder.completed_at && <Descriptions.Item label="Completed">{dayjs(viewOrder.completed_at).format('MMM D, YYYY HH:mm')}</Descriptions.Item>}
                        </Descriptions>

                        {viewOrder.status === 'COMPLETED' && (
                            <Row gutter={16} style={{ marginBottom: 20 }}>
                                <Col span={8}><Statistic title="Wet Weight" value={viewOrder.total_wet_weight} suffix="kg" precision={2} /></Col>
                                <Col span={8}><Statistic title="Dried Weight" value={viewOrder.total_dried_weight} suffix="kg" precision={2} valueStyle={{ color: '#059669' }} /></Col>
                                <Col span={8}><Statistic title="Drying Loss" value={viewOrder.drying_loss_percent} suffix="%" precision={2} valueStyle={{ color: '#dc2626' }} /></Col>
                            </Row>
                        )}

                        <Divider orientation="left" orientationMargin={0}>Lots</Divider>
                        {viewOrder.lots?.map(lot => (
                            <Card key={lot.id} size="small" style={{ marginBottom: 8, borderRadius: 8 }}>
                                <Row gutter={16}>
                                    <Col span={6}><Text strong>{lot.palay_lot?.lot_number}</Text></Col>
                                    <Col span={6}><Text type="secondary">Wet: </Text><Text strong>{fmt(lot.wet_weight)} kg</Text></Col>
                                    <Col span={6}><Text type="secondary">Initial MC: </Text><Text>{lot.initial_mc != null ? `${lot.initial_mc}%` : 'Not measured'}</Text></Col>
                                    <Col span={6}><Text type="secondary">Expected: </Text><Text style={{ color: '#2563eb' }}>{lot.expected_dried_weight != null ? `${fmt(lot.expected_dried_weight)} kg` : 'Not calculated'}</Text></Col>
                                </Row>
                                {lot.dried_weight != null && (
                                    <Row gutter={16} style={{ marginTop: 8 }}>
                                        <Col span={6}><Text type="secondary">Actual Dried: </Text><Text style={{ color: '#059669' }} strong>{fmt(lot.dried_weight)} kg</Text></Col>
                                        <Col span={6}><Text type="secondary">Final MC: </Text><Text>{lot.final_mc != null ? `${lot.final_mc}%` : 'Not measured'}</Text></Col>
                                        <Col span={6}><Text type="secondary">Loss: </Text><Text style={{ color: '#dc2626' }}>{fmt(lot.drying_loss)} kg ({fmt(lot.drying_loss_percent)}%)</Text></Col>
                                    </Row>
                                )}
                            </Card>
                        ))}
                    </div>
                )}
            </Modal>

            {/* Complete Drying Modal */}
            <Modal
                open={completeOpen}
                onCancel={() => setCompleteOpen(false)}
                title={`Complete Drying — ${completeOrder?.drying_number}`}
                footer={null}
                width={680}
            >
                {completeOrder && (
                    <Form form={completeForm} onFinish={submitComplete} layout="vertical">
                        <div style={{ background: '#f0fdf4', borderRadius: 8, padding: '12px 16px', marginBottom: 20 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Enter the actual dried weight from your scale. Final moisture content is optional—leave it blank when no moisture meter is available.
                            </Text>
                        </div>
                        {completeOrder.lots?.map((lot, idx) => (
                            <Card key={lot.id} size="small" style={{ marginBottom: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Text strong style={{ fontSize: 14 }}>
                                        {lot.palay_lot?.lot_number}
                                        <Text type="secondary" style={{ fontWeight: 400, marginLeft: 8 }}>
                                            {lot.palay_lot?.purchase?.partner?.name}
                                        </Text>
                                    </Text>
                                    <Space>
                                        <Tag>Wet: {fmt(lot.wet_weight)} kg</Tag>
                                        <Tag>Initial MC: {lot.initial_mc != null ? `${lot.initial_mc}%` : 'Not measured'}</Tag>
                                        {lot.expected_dried_weight != null && <Tag color="blue">Expected: {fmt(lot.expected_dried_weight)} kg</Tag>}
                                    </Space>
                                </div>
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            label="Actual Dried Weight (kg)"
                                            name={`dried_weight_${lot.id}`}
                                            rules={[{ required: true, message: 'Required' }]}
                                        >
                                            <InputNumber
                                                min={0.001}
                                                max={lot.wet_weight}
                                                step={0.001}
                                                style={{ width: '100%' }}
                                                addonAfter="kg"
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            label="Final Moisture Content (%) — Optional"
                                            name={`final_mc_${lot.id}`}
                                        >
                                            <InputNumber
                                                min={0}
                                                max={99}
                                                step={0.1}
                                                style={{ width: '100%' }}
                                                addonAfter="%"
                                                placeholder="Not measured"
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>
                        ))}
                        <Form.Item style={{ marginBottom: 0, marginTop: 16 }}>
                            <Space>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={actionLoading}
                                    icon={<CheckCircleOutlined />}
                                    style={{ height: 40, borderRadius: 8, background: '#059669', borderColor: '#059669' }}
                                >
                                    Confirm Drying Complete
                                </Button>
                                <Button onClick={() => setCompleteOpen(false)} style={{ height: 40, borderRadius: 8 }}>Cancel</Button>
                            </Space>
                        </Form.Item>
                    </Form>
                )}
            </Modal>
        </div>
    );
};

export default DryingPage;
